import { ReactElement, ReactNode } from "react";
import { ListCard } from "./Card";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router";

export default function List<T extends { id: string }>({
  list,
  columns,
  clickRow,
  rowHref,
  header = true,
  emptyState,
  noBorder = false,
  isLoading = false,
}: {
  list: T[];
  columns: Column<T>[];
  clickRow?: (row: T) => void;
  header?: boolean;
  noBorder?: boolean;
  emptyState?: ReactElement;
  isLoading?: boolean;
  rowHref?: (row: T) => string;
}) {
  const commonBorderClass = "border-slate-300";
  const innerBorderClass = noBorder
    ? ""
    : `not-last:border-b ${commonBorderClass}`;

  return (
    <ListCard borderless={noBorder}>
      <table className="w-full bg-white">
        {header && (
          <thead className="sticky bg-white top-0 font-bold text-sm shadow-sm shadow-slate-300">
            <tr>
              {columns.map(({ label }, i) => (
                <th key={i} scope="col" className={`text-left px-4 py-3`}>
                  {label || ""}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="text-sm">
          {list.map((row) => (
            <Row<T>
              key={row.id}
              data={row}
              clickRow={clickRow}
              rowHref={rowHref}
              columns={columns}
              listData={list}
              innerBorderClass={innerBorderClass}
            />
          ))}
          {list.length === 0 &&
            isLoading &&
            Array.from({ length: 2 }).map((_, id) => (
              <tr className={innerBorderClass} key={id}>
                <td colSpan={columns.length} className="px-4 py-3">
                  <Skeleton count={1} height={32} />
                </td>
              </tr>
            ))}
          {list.length === 0 && !isLoading && emptyState && (
            <tr className={innerBorderClass}>
              <td colSpan={columns.length} className="px-4 py-3">
                {emptyState}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </ListCard>
  );
}

function Row<T extends { id: string }>({
  data,
  clickRow,
  columns,
  listData,
  innerBorderClass,
  rowHref,
}: {
  data: T;
  clickRow?: (data: T) => void;
  columns: Column<T>[];
  listData: T[];
  innerBorderClass: string;
  rowHref?: (row: T) => string;
}) {
  return (
    <tr
      className={`${!!clickRow && "cursor-pointer"} ${innerBorderClass}`}
      onClick={() => clickRow && clickRow(data)}
    >
      {columns.map(({ content, className = "", skipLink = false }, i) => {
        const isLink = rowHref && !skipLink;
        if (isLink) {
          return (
            <td key={i}>
              <Link className={`block ${className}`} to={rowHref(data)}>
                {content(data, listData)}
              </Link>
            </td>
          );
        }

        return (
          <td key={i} className={`px-4 py-3 ${className}`}>
            {content(data, listData)}
          </td>
        );
      })}
    </tr>
  );
}

export interface Column<T> {
  label?: ReactNode;
  content: (row: T, list?: T[]) => ReactNode;
  className?: string;
  skipLink?: boolean;
}
